export const BOOKING_BILLING_ADDRESS_RETURN_TARGET = {
  PRODUCTS: "products",
  SUMMARY: "summary",
};

export const isBillingAddressRequired = (selectedProducts) =>
  Array.isArray(selectedProducts) && selectedProducts.length > 0;

export const getBillingAddressReturnTarget = (returnTarget) =>
  returnTarget === BOOKING_BILLING_ADDRESS_RETURN_TARGET.SUMMARY
    ? BOOKING_BILLING_ADDRESS_RETURN_TARGET.SUMMARY
    : BOOKING_BILLING_ADDRESS_RETURN_TARGET.PRODUCTS;

export const shouldShowBillingAddressReturnPrompt = (returnTarget) =>
  getBillingAddressReturnTarget(returnTarget) ===
  BOOKING_BILLING_ADDRESS_RETURN_TARGET.SUMMARY;

export const buildSectionActionItems = ({
  customerInfoComplete,
  scheduleComplete,
  billingAddressRequired,
  billingAddressComplete,
  customerInfoSectionRef,
  dateTimeSectionRef,
  productsSectionRef,
  serviceLocationComplete,
  serviceLocationSectionRef,
}) =>
  [
    !customerInfoComplete
      ? { label: "Complete your info", ref: customerInfoSectionRef }
      : null,
    serviceLocationSectionRef && !serviceLocationComplete
      ? { label: "Finish service location", ref: serviceLocationSectionRef }
      : null,
    !scheduleComplete
      ? { label: "Select date and time", ref: dateTimeSectionRef }
      : null,
    billingAddressRequired && !billingAddressComplete
      ? { label: "Add billing address", ref: productsSectionRef }
      : null,
  ].filter(Boolean);

export const buildSummaryBlockers = ({
  customerInfoComplete,
  scheduleComplete,
  billingAddressRequired,
  billingAddressComplete,
  customerInfoSectionRef,
  dateTimeSectionRef,
  productsSectionRef,
  serviceLocationComplete,
  serviceLocationSectionRef,
}) =>
  [
    !customerInfoComplete
      ? {
          key: "customerInfo",
          label: "Complete your information",
          ref: customerInfoSectionRef,
        }
      : null,
    serviceLocationSectionRef && !serviceLocationComplete
      ? {
          key: "serviceLocation",
          label: "Finish service location",
          ref: serviceLocationSectionRef,
        }
      : null,
    !scheduleComplete
      ? {
          key: "dateTime",
          label: "Finish Date & Time",
          ref: dateTimeSectionRef,
        }
      : null,
    billingAddressRequired && !billingAddressComplete
      ? {
          key: "billingAddress",
          label: "Add billing address",
          ref: productsSectionRef,
          requiresBillingAddressFocus: true,
        }
      : null,
  ].filter(Boolean);

export const buildSummarySoftWarnings = ({
  selectedProducts,
  waitlistOnlyPath,
  serviceLocation,
  travelFee,
}) =>
  [
    selectedProducts.length === 0
      ? { key: "no-products", label: "No products selected (optional)." }
      : null,
    serviceLocation === "client" && travelFee > 0
      ? {
          key: "travel-fee",
          label: `Travel fee applies for mobile service ($${travelFee}).`,
        }
      : null,
    waitlistOnlyPath
      ? {
          key: "waitlist-path",
          label:
            "Waitlist path active. Checkout is deferred until a slot becomes available.",
        }
      : null,
  ].filter(Boolean);

export const getNextSectionKey = (sectionSequence, sectionKey) => {
  const currentIndex = sectionSequence.indexOf(sectionKey);
  if (currentIndex === -1 || currentIndex === sectionSequence.length - 1) {
    return sectionKey;
  }

  return sectionSequence[currentIndex + 1];
};

export const getCollapsibleSectionStyle = (expandedSection, sectionKey) =>
  expandedSection === sectionKey
    ? {}
    : {
        display: "none",
      };

export const getSectionPillStyle = (state) => {
  if (state === "complete") {
    return {
      padding: "6px 10px",
      fontSize: "12px",
      borderRadius: "999px",
      border: "1px solid #86efac",
      background: "#ecfdf5",
      color: "#166534",
      cursor: "pointer",
    };
  }
  if (state === "attention") {
    return {
      padding: "6px 10px",
      fontSize: "12px",
      borderRadius: "999px",
      border: "1px solid #fcd34d",
      background: "#fffbeb",
      color: "#92400e",
      cursor: "pointer",
    };
  }
  if (state === "optional") {
    return {
      padding: "6px 10px",
      fontSize: "12px",
      borderRadius: "999px",
      border: "1px solid #bfdbfe",
      background: "#eff6ff",
      color: "#1d4ed8",
      cursor: "pointer",
    };
  }
  return {
    padding: "6px 10px",
    fontSize: "12px",
    borderRadius: "999px",
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    cursor: "pointer",
  };
};

export const getSectionBlockStyle = (state) => {
  if (state === "complete") {
    return {
      border: "1px solid #bbf7d0",
      background: "#f0fdf4",
    };
  }
  if (state === "attention") {
    return {
      border: "1px solid #fde68a",
      background: "#fffbeb",
    };
  }
  if (state === "optional") {
    return {
      border: "1px solid #bfdbfe",
      background: "#eff6ff",
    };
  }
  return {};
};

export const getSectionStatusMeta = (state) => {
  if (state === "complete") {
    return {
      label: "Complete",
      background: "#dcfce7",
      color: "#166534",
      border: "#86efac",
      guidance: "",
    };
  }
  if (state === "attention") {
    return {
      label: "Needs Attention",
      background: "#fef3c7",
      color: "#92400e",
      border: "#fcd34d",
      guidance: "",
    };
  }
  if (state === "optional") {
    return {
      label: "Optional",
      background: "#dbeafe",
      color: "#1d4ed8",
      border: "#93c5fd",
      guidance: "",
    };
  }

  return {
    label: "Required",
    background: "#f3f4f6",
    color: "#374151",
    border: "#d1d5db",
    guidance: "Required before final checkout submission.",
  };
};